import type { Facility, GroupForExport } from '@schooly/api';
import { FacilityType, GroupType } from '@schooly/api';
import type { Row, XlsColumn } from '@schooly/components/xls-service';
import { DATE_FORMAT } from '@schooly/constants';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';

import { getUserFullName } from '../../../../../helpers/users';

import type { TimetableImportMessages } from './useTimetableImportMessages';

export function buildGroupExportColumns(msg: TimetableImportMessages): XlsColumn[] {
  return [
    { key: 'name', header: msg.columnGroupName, width: 25 },
    { key: 'validity', header: msg.columnValidity, width: 20 },
    { key: 'subject', header: msg.columnSubject, width: 20 },
    { key: 'gag', header: msg.columnGag, width: 20 },
    { key: 'timetableCode', header: msg.columnTimetableCode, width: 20 },
    { key: 'defaultRoom', header: msg.columnDefaultRoom, width: 25 },
    { key: 'staff', header: msg.columnStaff, width: 25 },
  ];
}

export function getValidityExportValue(validity: GroupForExport['validity']): string {
  const { date_from, date_to } = validity;
  if (!date_from || !date_to) return '';

  return `${format(newDateTimezoneOffset(date_from), DATE_FORMAT)} - ${format(
    newDateTimezoneOffset(date_to),
    DATE_FORMAT,
  )}`;
}

export function getSubjectExportValue(
  type: GroupType | undefined,
  subjectName: string | null | undefined,
  msg: TimetableImportMessages,
): string {
  switch (type) {
    case GroupType.TutorGroup:
      return msg.subjectTutorGroup;
    case GroupType.Subject:
      return subjectName ?? '';
    default:
      return msg.subjectNone;
  }
}

function buildRoomIdToPathMap(facilities: Facility[]): Map<string, string> {
  const byId = new Map(facilities.map((f) => [f.id, f]));
  const idToPath = new Map<string, string>();

  for (const f of facilities) {
    if (f.type !== FacilityType.Room) continue;

    const parts: string[] = [f.name];
    let current = f.parent_id ? byId.get(f.parent_id) : null;

    while (current) {
      parts.unshift(current.name);
      current = current.parent_id ? byId.get(current.parent_id) : null;
    }

    idToPath.set(f.id, parts.join(';'));
  }

  return idToPath;
}

type BuildGroupExportRowsParams = {
  groups: GroupForExport[];
  facilities: Facility[];
  msg: TimetableImportMessages;
};

export function buildGroupExportRows({
  groups,
  facilities,
  msg,
}: BuildGroupExportRowsParams): Row[] {
  const roomIdToPath = buildRoomIdToPathMap(facilities);

  return groups.map((g) => ({
    name: g.name,
    validity: getValidityExportValue(g.validity),
    subject: getSubjectExportValue(g.group_type, g.subject?.name, msg),
    gag: g.age_groups?.map((ag) => ag.name).join(', ') ?? '',
    timetableCode: g.timetable_code ?? '',
    defaultRoom: g.default_room_id ? roomIdToPath.get(g.default_room_id) : '',
    staff: g.staff.length ? g.staff.map((s) => getUserFullName(s)).join(', ') : '',
  }));
}
